import { useMemo } from 'react';
import { useLoading } from 'react-simplikit';

import useProductStore from '@/features/Product/store/ProductStore';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { toast } from '@/stores/pure/ToastStore';
import useTagStore from '@/stores/TagStore';
import { FeedbackSourceMap } from '@/types/feedbackConst';
import { FEEDBACK_COLUMNS, PRODUCT_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import type { ScrapingJob } from '../api';
import { getScraperConfigSafe } from '../configs/config-actions';
import { SCRAPER_REQ_FIELD_KEY } from '../configs/scraper-field-map/field-const';
import {
  SCRAPER_CATEGORY,
  SCRAPING_JOB_STATUS,
  type SCRAPING_JOB_TYPE,
} from '../types';

export const useScrapingJobDetailAction = (
  scrapingJob: ScrapingJob,
  callbacks: {
    refetchCallback: () => void;
    onConfirmCallback: (job_type: SCRAPING_JOB_TYPE) => void;
    onCloseCallback?: () => void;
  },
) => {
  const { refetchCallback, onConfirmCallback, onCloseCallback } = callbacks;
  const setPopup = usePopup();
  const [loading, startLoading] = useLoading();
  const newChat = useHeySynclyStore((state) => state.newChat);
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  const productMap = useProductStore((state) => state.productMap);
  const tagMap = useTagStore((state) => state.tagMap);
  const wr = useWorkspaceRouter();

  const onDeleteScheduleClick = async () => {
    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Are you sure you want to delete this schedule?',
      message:
        'The automated scraping will be cancelled and no new data will be collected.',
      onConfirm: async () => {
        try {
          await service.scraping.deleteScrapingJob([scrapingJob.alias]);
          refetchCallback();
          toast('Schedule deleted successfully', 'success');
          onCloseCallback?.();
        } catch (e) {
          toast('Failed to delete schedule. Please try again.', 'fail');
        }
      },
    });
  };

  const onCancelJobClick = async () => {
    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Are you sure you want to cancel the job?',
      message: 'This action cannot be undone.',
      onConfirm: async () => {
        try {
          await service.scraping.cancelScrapingJob([scrapingJob.alias]);
          refetchCallback();
          toast('Job canceled successfully', 'success');
          onCloseCallback?.();
        } catch (e) {
          toast('Failed to cancel job. Please try again.', 'fail');
        }
      },
    });
  };

  const onDeleteAllFeedbackClick = async () => {
    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Are you sure you want to delete all feedback from this job?',
      renderContent({ onDataChange }) {
        return (
          <div className="flex flex-col flex-1 w-full py-2 gap-4">
            <p className="text-lg text-gray-600">
              This will remove all feedback collected from this scraping request
              due to data quality issues.
            </p>
            <textarea
              rows={3}
              onChange={(e) => {
                onDataChange(e.target.value);
              }}
              className="py-2 px-3 rounded border border-gray-300 flex-1"
              placeholder="Type reason"
            />
          </div>
        );
      },
      confirmText: 'Delete all',
      onConfirm: async (data) => {
        if (!data) {
          toast('Please enter the reason for deletion.', 'fail');
          return;
        }
        try {
          await service.scraping.deleteFeedbackByScrapingJob(
            scrapingJob.alias,
            data,
          );
          refetchCallback();
          toast('Feedback deleted successfully', 'success');
          onCloseCallback?.();
        } catch (e) {
          toast('Failed to delete feedback. Please try again.', 'fail');
        }
      },
    });
  };

  const onConfirmClick = async () => {
    await startLoading(service.scraping.confirmScrapingJob(scrapingJob.alias));
    refetchCallback();
    onConfirmCallback(scrapingJob.type);
  };

  const commerceCategoryFilterBuilder = () => {
    const sFilter = new SFilter();
    sFilter.setDateFilter({
      start_date: scrapingJob.start_date || null,
      end_date: scrapingJob.end_date || null,
    });

    // product name filter
    if (scrapingJob.scraper_input.product_name) {
      const matchingBrand = brandMap
        .values()
        .find((brand) => brand.name === scrapingJob.scraper_input.brand);

      const matchingLine = lineMap
        .values()
        .find((line) => line.name === scrapingJob.scraper_input.product_line);

      const product = productMap.values().find((product) => {
        const isProductNameMatch =
          product.product_name === scrapingJob.scraper_input.product_name;

        // brand가 존재하지 않으면 pass
        const isBrandMatch =
          !matchingBrand || product.brand_id === matchingBrand.id;

        // product line이 존재하지 않으면 pass
        const isLineMatch =
          !matchingLine || product.line_id === matchingLine.id;

        return isProductNameMatch && isBrandMatch && isLineMatch;
      });
      if (product) {
        sFilter.addValueSet(PRODUCT_COLUMNS.PRODUCT, {
          op: STRUCTURED_FILTER_OPERATOR.OR,
          values: [product.product_id],
        });
      }
    }

    // product line filter
    if (scrapingJob.scraper_input.product_line) {
      const line = lineMap
        .values()
        .find((line) => line.name === scrapingJob.scraper_input.product_line);
      if (line) {
        sFilter.addValueSet(PRODUCT_COLUMNS.LINE, {
          op: STRUCTURED_FILTER_OPERATOR.OR,
          values: [line.id],
        });
      }
    }

    // product brand filter
    if (scrapingJob.scraper_input.brand) {
      const brand = brandMap
        .values()
        .find((brand) => brand.name === scrapingJob.scraper_input.brand);
      if (brand) {
        sFilter.addValueSet(PRODUCT_COLUMNS.BRAND, {
          op: STRUCTURED_FILTER_OPERATOR.OR,
          values: [brand.id],
        });
      }
    }

    return sFilter;
  };

  const keywordScrapperFilterBuilder = () => {
    const sFilter = new SFilter();
    sFilter.setDateFilter({
      start_date: scrapingJob.start_date || null,
      end_date: scrapingJob.end_date || null,
    });

    if (scrapingJob.scraper_input.keyword) {
      const tag = Object.values(tagMap).find(
        (tag) => tag.name === scrapingJob.scraper_input.keyword,
      );
      if (tag) {
        sFilter.addValueSet(FEEDBACK_COLUMNS.TAG, {
          op: STRUCTURED_FILTER_OPERATOR.OR,
          values: [tag.alias],
        });
      }
    }

    const source = FeedbackSourceMap.get(scrapingJob.source_type);

    if (source) {
      sFilter.addValueSet(FEEDBACK_COLUMNS.SOURCE_INFO, {
        op: STRUCTURED_FILTER_OPERATOR.OR,
        values: [source],
      });
    }

    return sFilter;
  };

  const onSummarizeWithHeySynclyClick = () => {
    const scraperConfig = getScraperConfigSafe(scrapingJob.scraper_name);

    if (!scraperConfig) {
      return;
    }

    if (scraperConfig.fields.some((v) => v === SCRAPER_REQ_FIELD_KEY.KEYWORD)) {
      newChat(keywordScrapperFilterBuilder());
    }

    if (scraperConfig.category === SCRAPER_CATEGORY.COMMERCE) {
      newChat(commerceCategoryFilterBuilder());
    }

    wr.push(`/hey-syncly`);
    refetchCallback();
  };

  const heySynclyEnabled = useMemo(() => {
    const scraperConfig = getScraperConfigSafe(scrapingJob.scraper_name);

    if (!scraperConfig) {
      return false;
    }

    if (scrapingJob.status === SCRAPING_JOB_STATUS.COMPLETED) {
      if (
        scraperConfig.category === SCRAPER_CATEGORY.COMMERCE ||
        scraperConfig.fields.some((v) => v === SCRAPER_REQ_FIELD_KEY.KEYWORD)
      ) {
        return true;
      }
    }

    return false;
  }, [scrapingJob.status, scrapingJob.scraper_name]);

  return {
    loading,
    onDeleteScheduleClick,
    onCancelJobClick,
    onDeleteAllFeedbackClick,
    onConfirmClick,
    onSummarizeWithHeySynclyClick,
    heySynclyEnabled,
  };
};
